import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { BRAND } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import {
  useCreateCouponMutation,
  useGetCouponQuery,
  useUpdateCouponMutation,
} from '@/modules/coupons/couponsApi';
import type { CouponDiscountType } from '@/types/coupon';
import { COUPON_DISCOUNT_TYPE_OPTIONS } from '@/types/coupon';
import { resolveMutationResult } from '@/utils/apiResponse';
import { buildCouponCodePreview } from '@/utils/couponCode';
import styles from './CouponForm.module.scss';

interface CouponFormValues {
  code: string;
  description: string;
  discountType: CouponDiscountType;
  discountValue: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  usageLimit?: number;
  perUserLimit?: number;
  startsAt?: Dayjs | null;
  expiresAt?: Dayjs | null;
  isActive: boolean;
}

export default function CouponForm() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { data: coupon, isLoading: isLoadingCoupon } = useGetCouponQuery(id ?? '', {
    skip: !isEdit || !id,
  });
  const [createCoupon, createState] = useCreateCouponMutation();
  const [updateCoupon, updateState] = useUpdateCouponMutation();
  const isSaving = createState.isLoading || updateState.isLoading;

  useGlobalLoader(isSaving);
  useGlobalLoader(isEdit && isLoadingCoupon);

  const [isCodeManual, setIsCodeManual] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm<CouponFormValues>({
    defaultValues: {
      code: '',
      description: '',
      discountType: 'percentage',
      discountValue: 10,
      isActive: true,
    },
  });

  const discountType = watch('discountType');
  const description = watch('description');
  const codePreview = useMemo(() => buildCouponCodePreview(description), [description]);

  useEffect(() => {
    if (isEdit || isCodeManual) return;
    setValue('code', codePreview);
  }, [codePreview, isCodeManual, isEdit, setValue]);

  useEffect(() => {
    if (!isEdit || !coupon) return;

    setIsCodeManual(true);
    reset({
      code: coupon.code,
      description: coupon.description ?? '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      usageLimit: coupon.usageLimit,
      perUserLimit: coupon.perUserLimit,
      startsAt: coupon.startsAt ? dayjs(coupon.startsAt) : null,
      expiresAt: coupon.expiresAt ? dayjs(coupon.expiresAt) : null,
      isActive: coupon.isActive,
    });
  }, [coupon, isEdit, reset]);

  const onSubmit = async (values: CouponFormValues) => {
    const trimmedCode = values.code.trim().toUpperCase();
    const payload = {
      ...(trimmedCode ? { code: trimmedCode } : {}),
      description: values.description.trim(),
      discountType: values.discountType,
      discountValue: values.discountValue,
      minOrderAmount: values.minOrderAmount,
      maxDiscountAmount: values.maxDiscountAmount,
      usageLimit: values.usageLimit,
      perUserLimit: values.perUserLimit,
      startsAt: values.startsAt ? values.startsAt.toISOString() : undefined,
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!id) return;
      const result = await resolveMutationResult(updateCoupon({ id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? 'Coupon updated successfully.');
        navigate(ROUTES.COUPONS);
        return;
      }
      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createCoupon(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Coupon created successfully.');
      navigate(ROUTES.COUPONS);
      return;
    }

    notify.error(result.message);
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.COUPONS)}>
              Back
            </Button>
            <Button type="primary" loading={isSaving} onClick={handleSubmit(onSubmit)}>
              {isEdit ? 'Save coupon' : 'Create coupon'}
            </Button>
          </>
        }
      />

      <Card loading={isEdit && isLoadingCoupon} className={styles.card}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <div className={styles.gridTwo}>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Form.Item label="Code">
                  <Input
                    {...field}
                    placeholder="Auto-generated from description"
                    className={styles.codeInput}
                    onChange={(event) => {
                      setIsCodeManual(true);
                      field.onChange(event.target.value.toUpperCase());
                    }}
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Form.Item label="Description">
                  <Input {...field} placeholder="e.g. Summer sale discount" />
                </Form.Item>
              )}
            />
          </div>

          <div className={styles.gridThree}>
            <Controller
              name="discountType"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.Item label="Discount type" required>
                  <Select {...field} options={COUPON_DISCOUNT_TYPE_OPTIONS} />
                </Form.Item>
              )}
            />

            <Controller
              name="discountValue"
              control={control}
              rules={{
                required: 'Discount value is required',
                min: { value: 0, message: 'Must be 0 or greater' },
                max:
                  discountType === 'percentage'
                    ? { value: 100, message: 'Cannot exceed 100%' }
                    : undefined,
              }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label={discountType === 'percentage' ? 'Discount (%)' : 'Discount amount'}
                  required
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <InputNumber
                    {...field}
                    min={0}
                    max={discountType === 'percentage' ? 100 : undefined}
                    className={styles.fullWidth}
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Form.Item label="Status">
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    style={{ background: field.value ? BRAND.primary : undefined }}
                  />
                </Form.Item>
              )}
            />
          </div>

          <div className={styles.gridThree}>
            <Controller
              name="minOrderAmount"
              control={control}
              render={({ field }) => (
                <Form.Item label="Min order amount">
                  <InputNumber
                    {...field}
                    min={0}
                    className={styles.fullWidth}
                    placeholder="Optional"
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="maxDiscountAmount"
              control={control}
              render={({ field }) => (
                <Form.Item label="Max discount cap">
                  <InputNumber
                    {...field}
                    min={0}
                    className={styles.fullWidth}
                    placeholder="Optional"
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="usageLimit"
              control={control}
              render={({ field }) => (
                <Form.Item label="Total usage limit">
                  <InputNumber
                    {...field}
                    min={0}
                    className={styles.fullWidth}
                    placeholder="Unlimited"
                  />
                </Form.Item>
              )}
            />
          </div>

          <div className={styles.gridThree}>
            <Controller
              name="perUserLimit"
              control={control}
              render={({ field }) => (
                <Form.Item label="Per user limit">
                  <InputNumber
                    {...field}
                    min={0}
                    className={styles.fullWidth}
                    placeholder="Unlimited"
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="startsAt"
              control={control}
              render={({ field }) => (
                <Form.Item label="Starts at">
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    showTime
                    className={styles.fullWidth}
                    placeholder="Optional"
                  />
                </Form.Item>
              )}
            />

            <Controller
              name="expiresAt"
              control={control}
              render={({ field }) => (
                <Form.Item label="Expires at">
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    showTime
                    className={styles.fullWidth}
                    placeholder="Optional"
                  />
                </Form.Item>
              )}
            />
          </div>
        </Form>
      </Card>
    </div>
  );
}
